import { lazy, type LazyExoticComponent, type ComponentType } from 'react';

/**
 * Shell Registry — single source of truth for every selectable UI layer.
 *
 * Adding a new shell:
 *   1. Create `src/shells/<id>/Shell.tsx` exporting `XxxShell`.
 *   2. Append a manifest entry below.
 * That's it — IndexRouter and the in-app switchers are registry-driven and
 * will pick the new shell up automatically.
 *
 * AI agents prototyping a competitor mimic should set `mimics: '<Product>'`
 * and `status: 'experimental'` so the entry is visibly not for production use.
 */

export type ShellStatus = 'stable' | 'experimental' | 'draft';

export interface LocalizedString {
  en: string;
  nl: string;
}

export interface ShellManifest {
  /** Stable id — written to localStorage and used in URLs (`?shell=<id>`). */
  id: string;
  /** Display name shown in switchers. Self-contained — no i18n.json entry required. */
  name: LocalizedString;
  /** Short tagline shown under the name. Optional. */
  description?: LocalizedString;
  /** Competitor or product this shell intentionally borrows from. */
  mimics?: string;
  /** Lifecycle stage. Experimental shells may be hidden from non-expert switchers later. */
  status: ShellStatus;
  /**
   * Lazy-loaded shell component.
   * `null` means "use the classic Index page" — reserved for the `clando` entry.
   */
  // ComponentType<any> instead of <unknown> because lazy() of a component with
  // its own props won't satisfy ComponentType<unknown>.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: LazyExoticComponent<ComponentType<any>> | null;
}

// Internal: keeps literal `id` types so `UIShell` can be derived below.
// The exported `SHELLS` widens to `ShellManifest[]` so iteration sees the full
// optional shape (otherwise `mimics`/`description` only exist on entries that
// happen to define them).
const _SHELLS = [
  {
    id: 'clando',
    name: { en: 'Classic Clando', nl: 'Klassiek Clando' },
    description: {
      en: 'The original tab-based household UI.',
      nl: 'De originele tab-gestuurde huishoud-UI.',
    },
    status: 'stable',
    Component: null,
  },
  {
    id: 'clando-next',
    name: { en: 'New UI', nl: 'Nieuwe UI' },
    description: {
      en: 'Calmer sidebar layout with a focused Today surface.',
      nl: 'Rustiger sidebar-lay-out met focus op Vandaag.',
    },
    status: 'experimental',
    Component: lazy(() =>
      import('./clando-next/ClandoNextShell').then((m) => ({ default: m.ClandoNextShell }))
    ),
  },
  {
    id: 'msto',
    name: { en: 'MSTO', nl: 'MSTO' },
    description: {
      en: 'Microsoft To Do-style sidebar, smart lists and detail pane.',
      nl: 'Microsoft To Do-stijl sidebar, slimme lijsten en detailpaneel.',
    },
    mimics: 'Microsoft To Do',
    status: 'experimental',
    Component: lazy(() =>
      import('./msto/MstoShell').then((m) => ({ default: m.MstoShell }))
    ),
  },
  {
    id: 'rtm',
    name: { en: 'RTM', nl: 'RTM' },
    description: {
      en: 'Remember The Milk-style dense list and tag navigation.',
      nl: 'Remember The Milk-stijl dichte lijst met tagnavigatie.',
    },
    mimics: 'Remember The Milk',
    status: 'experimental',
    Component: lazy(() =>
      import('./rtm/RtmShell').then((m) => ({ default: m.RtmShell }))
    ),
  },
  {
    id: 'freelancer',
    name: { en: 'Freelancer', nl: 'Freelancer' },
    description: {
      en: 'Hours, projects and invoicing surfaces for solo work.',
      nl: 'Uren, projecten en facturen voor solo werk.',
    },
    status: 'experimental',
    Component: lazy(() =>
      import('./freelancer/FreelancerShell').then((m) => ({ default: m.FreelancerShell }))
    ),
  },
  {
    id: 'valuetime',
    name: { en: 'ValueTime', nl: 'ValueTime' },
    description: {
      en: 'Time-tracking-led variant with a green ValueTime palette.',
      nl: 'Tijdsregistratie-gerichte variant met ValueTime groen palet.',
    },
    mimics: 'ValueTime',
    status: 'experimental',
    Component: lazy(() =>
      import('./valuetime/ValuetimeShell').then((m) => ({ default: m.ValuetimeShell }))
    ),
  },
] as const satisfies readonly ShellManifest[];

/** Union of every registered shell id, derived from the registry. */
export type UIShell = (typeof _SHELLS)[number]['id'];

/**
 * Public, widened view of the registry — use this when iterating.
 * `id` stays typed as `UIShell` so it can flow into setters without casts.
 */
export const SHELLS: ReadonlyArray<ShellManifest & { id: UIShell }> = _SHELLS;

/** Default shell when no preference is stored. */
export const DEFAULT_SHELL: UIShell = 'clando';

export function getShell(id: string): ShellManifest | undefined {
  return SHELLS.find((s) => s.id === id);
}

export function isValidShellId(id: string): id is UIShell {
  return SHELLS.some((s) => s.id === id);
}

/**
 * Pick the localized name from a manifest. Falls back to English if the
 * requested language isn't an exact match (we only ship en/nl right now).
 */
export function shellName(manifest: ShellManifest, language: string): string {
  return language?.toLowerCase().startsWith('nl') ? manifest.name.nl : manifest.name.en;
}

export function shellDescription(
  manifest: ShellManifest,
  language: string
): string | undefined {
  if (!manifest.description) return undefined;
  return language?.toLowerCase().startsWith('nl')
    ? manifest.description.nl
    : manifest.description.en;
}
